import { Dispatch } from 'react';

import { type DateFormat, type TimeFormat } from '@ledium/types';

import lediumApi from '../../../utils/lediumApi';
import { GlobalState, UserData, ZONE_STORAGE_KEY } from '../state/initialGlobalState';

export const SET_LOADING = 'global/SET_LOADING';
export const API_FAILURE = 'global/API_FAILURE';
export const USER_LOGIN_API_REQUEST = 'global/user/USER_LOGIN_API_REQUEST';
export const USER_LOGIN_API_SUCCESS = 'global/user/USER_LOGIN_API_SUCCESS';
export const USER_LOGOUT_API_REQUEST = 'global/user/USER_LOGOUT_API_REQUEST';
export const USER_LOGOUT_API_SUCCESS = 'global/user/USER_LOGOUT_API_SUCCESS';
export const SET_DATE_TIME_FORMAT = 'global/SET_DATE_TIME_FORMAT';
export const SET_IS_ONLINE = 'global/SET_IS_ONLINE';
export const SET_SOCKET_CONNECTED = 'global/SET_SOCKET_CONNECTED';
export const SET_OFFLINE_MODE = 'global/SET_OFFLINE_MODE';
export const SET_SERVER_TIME_OFFSET = 'global/SET_SERVER_TIME_OFFSET';
export const SET_SELECTED_GROUP = 'global/SET_SELECTED_GROUP';
export const SET_PREVIOUS_GROUP = 'global/SET_PREVIOUS_GROUP';
export const SET_DASHBOARD_GROUP = 'global/SET_DASHBOARD_GROUP';

export type GlobalAction =
    | { type: typeof SET_LOADING; payload: boolean }
    | { type: typeof USER_LOGIN_API_REQUEST }
    | { type: typeof USER_LOGIN_API_SUCCESS; payload: Partial<UserData> }
    | { type: typeof API_FAILURE; payload: unknown }
    | { type: typeof USER_LOGOUT_API_REQUEST }
    | { type: typeof USER_LOGOUT_API_SUCCESS }
    | { type: typeof SET_DATE_TIME_FORMAT; payload: { dateFormat: DateFormat | null; timeFormat: TimeFormat | null } }
    | { type: typeof SET_IS_ONLINE; payload: boolean }
    | { type: typeof SET_SOCKET_CONNECTED; payload: boolean }
    | { type: typeof SET_OFFLINE_MODE; payload: boolean }
    | { type: typeof SET_SERVER_TIME_OFFSET; payload: number }
    | { type: typeof SET_SELECTED_GROUP; payload: string | null }
    | { type: typeof SET_PREVIOUS_GROUP; payload: string | null }
    | { type: typeof SET_DASHBOARD_GROUP; payload: string | null };

export const useGlobalActions = (getState: () => GlobalState, dispatch: Dispatch<GlobalAction>) => ({
    api: {
        setLoading: (payload: boolean) =>
            dispatch({
                type: SET_LOADING,
                payload
            }),
        setDateTimeFormat: (dateFormat: DateFormat, timeFormat: TimeFormat) =>
            dispatch({
                type: SET_DATE_TIME_FORMAT,
                payload: { dateFormat, timeFormat }
            }),
        setServerTimeOffset: (serverTime: number) =>
            dispatch({
                type: SET_SERVER_TIME_OFFSET,
                payload: serverTime - Date.now()
            })
    },
    users: {
        callUserLogin: async (data: unknown) => {
            dispatch({ type: USER_LOGIN_API_REQUEST });
            try {
                const response = await lediumApi.post<Partial<UserData>>('users/login', data);
                dispatch({ type: USER_LOGIN_API_SUCCESS, payload: response ?? {} });
            } catch (e) {
                dispatch({ type: API_FAILURE, payload: e });
            }
        },
        callUserLogout: async () => {
            dispatch({ type: USER_LOGOUT_API_REQUEST });
            try {
                await lediumApi.get('users/logout');
                dispatch({ type: USER_LOGOUT_API_SUCCESS });
            } catch (e) {
                dispatch({ type: API_FAILURE, payload: e });
            }
        }
    },
    zones: {
        setSelectedGroup: (id: string | null) => {
            if (id === null) {
                dispatch({ type: SET_PREVIOUS_GROUP, payload: getState().selectedGroupId });
                localStorage.removeItem(ZONE_STORAGE_KEY);
            } else {
                localStorage.setItem(ZONE_STORAGE_KEY, id);
            }
            dispatch({ type: SET_SELECTED_GROUP, payload: id });
        },
        restorePreviousGroup: () => {
            const id = getState().previousGroupId;
            if (id === null) {
                localStorage.removeItem(ZONE_STORAGE_KEY);
            } else {
                localStorage.setItem(ZONE_STORAGE_KEY, id);
            }
            dispatch({ type: SET_SELECTED_GROUP, payload: id });
            dispatch({ type: SET_PREVIOUS_GROUP, payload: null });
        },
        setDashboardGroup: (id: string | null) => {
            dispatch({ type: SET_DASHBOARD_GROUP, payload: id });
        }
    }
});
